import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";

const EMICalculator = () => {
  const minAmount = 50000;
  const maxAmount = 500000;

  const [loanType, setLoanType] = useState("home");
  const [amountPercentage, setAmountPercentage] = useState(0);
  const [loanAmount, setLoanAmount] = useState(minAmount);
  const [loanDuration, setLoanDuration] = useState(12);
  const [durationType, setDurationType] = useState("months");
  const [interestRate, setInterestRate] = useState(10);
  const [emi, setEmi] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  // Add refs for range sliders
  const amountSliderRef = useRef(null);
  const interestSliderRef = useRef(null);

  const calculatePercentage = (amount) =>
    Math.round(((amount - minAmount) / (maxAmount - minAmount)) * 100);

  const calculateAmount = (percentage) =>
    Math.round(minAmount + (percentage / 100) * (maxAmount - minAmount));

  const handlePercentageChange = (e) => {
    const newPercentage = Number(e.target.value);
    setAmountPercentage(newPercentage);
    setLoanAmount(calculateAmount(newPercentage));
  };

  useEffect(() => {
    setAmountPercentage(calculatePercentage(loanAmount));
  }, [loanAmount]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Update slider backgrounds when values change
  useEffect(() => {
    if (amountSliderRef.current) {
      const percentage = amountPercentage;
      amountSliderRef.current.style.background = `linear-gradient(to right, #007bff ${percentage}%, silver ${percentage}%)`;
    }
  }, [amountPercentage]);

  useEffect(() => {
    if (interestSliderRef.current) {
      const percentage = ((interestRate - 10) / 20) * 100;
      interestSliderRef.current.style.background = `linear-gradient(to right, #007bff ${percentage}%, silver ${percentage}%)`;
    }
  }, [interestRate]);

  const calculateEMI = () => {
    const monthlyInterestRate = interestRate / 1200;
    const totalMonths =
      durationType === "years" ? loanDuration * 12 : loanDuration;
    const emiValue =
      (loanAmount *
        monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, totalMonths)) /
      (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);

    const totalPayment = emiValue * totalMonths;
    const totalInterestPayment = totalPayment - loanAmount;

    setEmi(emiValue || 0);
    setTotalAmount(totalPayment || 0);
    setTotalInterest(totalInterestPayment || 0);
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, loanDuration, interestRate, durationType]);

  const pieData = [
    { name: "Principal Amount", value: totalAmount - totalInterest },
    { name: "Total Interest", value: totalInterest },
  ];

  const COLORS = ["#FF8C8B", "#8B96FF"];

  return (
    <>
      <div className="main-box mt-5">
        <div className="row ">
          <div className="col-12 col-md-12">
            <div
              className="Emi-section py-5 min-vh-80 d-flex align-items-center justify-content-center"
              style={{ backgroundColor: "#FAF9FF" }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <h4
                    className="text-center fw-semibold"
                    style={{ fontSize: "32px" }}
                  >
                    EMI Calculator
                  </h4>
                  <div className="col-12">
                    <div className="row">
                      <div className="col-12 col-md-6">
                        <div className="card-body p-4">
                          <div className="mb-4">
                            <p
                              className="form-label medium fw-medium text-secondary"
                              style={{ fontFamily: "poppins" }}
                            >
                              Loan Type
                            </p>
                            <select
                              className="form-select rounded-3"
                              value={loanType}
                              onChange={(e) => setLoanType(e.target.value)}
                            >
                              <option value="home">Home Loan</option>
                              <option value="personal">Personal Loan</option>
                              <option value="car">Car Loan</option>
                              <option value="education">Education Loan</option>
                            </select>
                          </div>

                          <div className="mb-4">
                            <p className="form-label small fw-medium text-secondary">
                              Loan Amount (₹)
                            </p>
                            <input
                              type="number"
                              className="form-control rounded-3 text-start"
                              style={{ fontFamily: "poppins" }}
                              value={loanAmount}
                              onChange={(e) =>
                                setLoanAmount(Number(e.target.value))
                              }
                            />
                            <div className="mt-2">
                              <input
                                type="range"
                                className="custom-range w-100 border-0"
                                style={{ fontFamily: "poppins" }}
                                min="0"
                                max="100"
                                step="1"
                                value={amountPercentage}
                                onChange={handlePercentageChange}
                                ref={amountSliderRef}
                              />
                              <div
                                className="d-flex justify-content-between text-start small text-primary mb-1"
                                style={{ fontFamily: "poppins" }}
                              >
                                <span>₹{minAmount.toLocaleString()}</span>
                                <span>₹{maxAmount.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mb-4">
                            <p
                              className="form-label small fw-medium text-secondary"
                              style={{ fontFamily: "poppins" }}
                            >
                              Loan Duration
                            </p>
                            <div className="input-group align-items-center">
                              <input
                                type="number"
                                className="form-control rounded-3 text-start"
                                value={loanDuration}
                                onChange={(e) =>
                                  setLoanDuration(Number(e.target.value))
                                }
                              />
                              <div
                                className="ms-2 position-relative rounded d-flex p-1"
                                style={{
                                  width: "130px",
                                  height: "34px",
                                  border: "0px solid #0d6efd", // active border color
                                  borderRadius: "999px",
                                  backgroundColor: "#ffffff",
                                  overflow: "hidden",
                                }}
                              >
                                {/* Sliding active background */}
                                <div
                                  className="position-absolute bg-primary rounded-pill"
                                  style={{
                                    width: "50%",
                                    height: "70%", // Adjust height as needed
                                    top: "15%", // (100% - 70%) / 2 to center vertically
                                    left:
                                      durationType === "months" ? "0%" : "50%",
                                    transition: "left 0.3s ease",
                                    zIndex: 0,
                                  }}
                                ></div>

                                {/* Mo button */}
                                <button
                                  type="button"
                                  className={`btn btn-sm fw-medium w-50 border-0 text-center ${
                                    durationType === "months"
                                      ? "text-white"
                                      : "text-primary"
                                  }`}
                                  onClick={() => setDurationType("months")}
                                  style={{
                                    background: "transparent",
                                    fontFamily: "poppins",
                                    zIndex: 1,
                                    borderRadius: "999px",
                                  }}
                                >
                                  Mo
                                </button>

                                {/* Yr button */}
                                <button
                                  type="button"
                                  className={`btn btn-sm fw-medium w-50 border-0 text-center ${
                                    durationType === "years"
                                      ? "text-white"
                                      : "text-primary"
                                  }`}
                                  onClick={() => setDurationType("years")}
                                  style={{
                                    background: "transparent",
                                    fontFamily: "poppins",
                                    zIndex: 1,
                                    borderRadius: "999px",
                                  }}
                                >
                                  Yr
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="mb-4">
                            <label
                              className="form-label small fw-medium text-secondary"
                              style={{ fontFamily: "poppins" }}
                            >
                              Rate of Interest
                            </label>
                            <input
                              type="number"
                              className="form-control rounded-3 text-start"
                              value={interestRate}
                              step="0.1"
                              onChange={(e) =>
                                setInterestRate(Number(e.target.value))
                              }
                            />
                          </div>
                          <div className="mt-2">
                            <div
                              className="d-flex justify-content-between small text-primary mb-1"
                              style={{ fontFamily: "poppins" }}
                            >
                              <span>10% p.a.</span>
                              <span>30% p.a.</span>
                            </div>
                            <input
                              type="range"
                              className="custom-range w-100"
                              min="10"
                              max="30"
                              step="0.1"
                              value={interestRate}
                              onChange={(e) =>
                                setInterestRate(Number(e.target.value))
                              }
                              ref={interestSliderRef}
                            />
                          </div>
                          <div className="d-flex justify-content-start justify-content-md-start mt-4 w-100 w-md-auto">
                            <Link
                              to="/"
                              className="btn text-white fw-semibold d-flex align-items-center px-3 px-md-4 py-1 py-md-2 rounded-4 border-0 bg-primary"
                              style={{
                                fontFamily: "poppins",
                                textDecoration: "none",
                              }}
                            >
                              Apply Now
                              <i
                                className="fas fa-arrow-right ms-2 ms-md-3"
                                style={{ color: "white", fontSize: "0.9rem" }}
                              ></i>
                            </Link>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mt-4 mb-5 mt-md-0">
                        <div className="card bg-white border-0 mt-5 h-100">
                          <div className="card-body">
                            <div className="row mb-4">
                              <div className="col-12 d-flex justify-content-center">
                                <PieChart width={200} height={200}>
                                  <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    dataKey="value"
                                  >
                                    {pieData.map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                        stroke="#fff"
                                        strokeWidth={2}
                                      />
                                    ))}
                                  </Pie>
                                </PieChart>
                              </div>
                            </div>

                            <div
                              className="emi-card mt-3 mx-auto"
                              style={{
                                backgroundColor: "#FAF9FF",
                                borderRadius: "5%",
                                width: "100%",
                                maxWidth: "380px",
                                padding: "1rem",
                              }}
                            >
                              <div className="row g-3">
                                <div className="col-12 col-md-6">
                                  <div
                                    className="card bg-opacity-10 border-0 h-100"
                                    style={{
                                      backgroundColor: "#FAF9FF",
                                      borderRadius: "5%",
                                    }}
                                  >
                                    <div className="card-body text-center d-flex flex-column justify-content-center h-100">
                                      <p className="small text-dark mb-1">
                                        Total Amount
                                      </p>
                                      <div className="d-flex justify-content-center align-items-center gap-2">
                                        <div
                                          style={{
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            backgroundColor: "#dc3545",
                                          }}
                                        ></div>
                                        <p
                                          className="h5 text-dark fw-bold mb-0"
                                          style={{ fontFamily: "poppins" }}
                                        >
                                          ₹{totalAmount.toFixed(2)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-6">
                                  <div
                                    className="card bg-opacity-10 border-0 h-100"
                                    style={{
                                      backgroundColor: "#FAF9FF",
                                      borderRadius: "5%",
                                    }}
                                  >
                                    <div className="card-body text-center d-flex flex-column justify-content-center h-100">
                                      <p className="small text-dark mb-1">
                                        Total Interest
                                      </p>
                                      <div className="d-flex justify-content-center align-items-center gap-2">
                                        <div
                                          style={{
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            backgroundColor: "blue",
                                          }}
                                        ></div>
                                        <p
                                          className="h5 text-dark fw-bold mb-0"
                                          style={{ fontFamily: "poppins" }}
                                        >
                                          ₹{totalInterest.toFixed(2)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* ✅ This section now visible on ALL devices including mobile */}
                            <div
                              className="mt-4 mx-auto"
                              style={{
                                borderRadius: "5%",
                                width: "100%",
                                maxWidth: "400px",
                                padding: "1rem",
                              }}
                            >
                              <div className="card bg-primary text-white">
                                <div className="card-body text-center">
                                  <p className="small mb-1 text-white">
                                    Monthly EMI
                                  </p>
                                  <p
                                    className="h4 text-white fw-bold mb-0"
                                    style={{ fontFamily: "poppins" }}
                                  >
                                    ₹{emi.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EMICalculator;
